// Corpus-free category definitions — safe for client bundles.
export type ConsularFaqCategory =
  | "apostille"
  | "fees"
  | "documents"
  | "process"
  | "embassy"
  | "translation"
  | "corporate";

export interface CategoryDef {
  slug: ConsularFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const CONSULAR_FAQ_CATEGORIES: CategoryDef[] = [
  {
    slug: "apostille",
    titleTh: "Apostille (มีผล 28 ก.พ. 2570) & อนุสัญญาเฮก",
    titleEn: "Apostille (in force 28 Feb 2027) & Hague Convention",
    descTh: "ไทยเข้าเป็นภาคีอนุสัญญา Apostille มีผล 28 ก.พ. 2570 — เอกสารใดใช้ Apostille แทนสถานทูตได้บ้าง",
    descEn: "Thailand's Hague Apostille Convention enters into force 28 Feb 2027 — which documents skip the embassy step.",
    matchTh: /(apostille|เฮก|hague|ภาคี|อนุสัญญา)/i,
    matchEn: /(apostille|hague|convention|member state)/i,
  },
  {
    slug: "fees",
    titleTh: "ค่าธรรมเนียมและระยะเวลา",
    titleEn: "Fees & Turnaround",
    descTh: "ค่าธรรมเนียมนิติกรณ์ 200/400 บาท ค่าบริการเดินเรื่อง และเวลาทำการ",
    descEn: "THB 200/400 legalization fees, agency pricing and processing times.",
    matchTh: /(ค่าธรรมเนียม|ราคา|กี่บาท|กี่วัน|ระยะเวลา|ใช้เวลา|ค่าบริการ)/,
    matchEn: /(fee|cost|price|how much|how long|turnaround|processing time|baht|thb)/i,
  },
  {
    slug: "documents",
    titleTh: "เอกสารที่ต้องเตรียม",
    titleEn: "Required Documents",
    descTh: "ฉบับจริง สำเนารับรองจากอำเภอ/เขต หนังสือมอบอำนาจ และสำเนาบัตรประชาชน",
    descEn: "Originals, district-certified copies, POA and ID requirements.",
    matchTh: /(เอกสาร|สำเนา|ฉบับจริง|มอบอำนาจ|บัตรประชาชน|พาสปอร์ต|ทะเบียน)/,
    matchEn: /(document|copy|original|power of attorney|poa|passport|id card|certificate)/i,
  },
  {
    slug: "process",
    titleTh: "ขั้นตอนและการจองคิว",
    titleEn: "Process & Queue Booking",
    descTh: "รวมขั้นตอนยื่นรับรองนิติกรณ์ที่กรมการกงสุล แจ้งวัฒนะ ทั้งจองคิวออนไลน์ e-Legalization ยื่นทางไปรษณีย์ และบริการด่วน พร้อมระยะเวลาและเอกสารที่ต้องเตรียมจริง",
    descEn: "How consular legalization is filed at Chaeng Watthana: e-Legalization online booking, walk-in queues, postal submission and express service, with realistic turnaround times and the exact paperwork required.",
    matchTh: /(ขั้นตอน|จองคิว|ยื่น|แจ้งวัฒนะ|ไปรษณีย์|ด่วน|walk|วิธี)/i,
    matchEn: /(process|step|book|queue|submit|chaeng|postal|express|urgent|how do|how to)/i,
  },
  {
    slug: "embassy",
    titleTh: "สถานทูตปลายทางและประเทศนอกภาคี",
    titleEn: "Destination Embassy & Non-Hague",
    descTh: "เส้นทาง MFA → สถานทูตปลายทาง สำหรับจีน เวียดนาม UAE และประเทศนอกภาคี",
    descEn: "MFA → destination embassy chain for China, Vietnam, UAE and other non-members.",
    matchTh: /(สถานทูต|กงสุลต่างประเทศ|จีน|เวียดนาม|ยูเออี|uae|นอกภาคี|ปลายทาง)/i,
    matchEn: /(embassy|consulate|china|vietnam|uae|non-hague|destination country)/i,
  },
  {
    slug: "translation",
    titleTh: "การแปลและรับรองคำแปล",
    titleEn: "Translation & Certified Copies",
    descTh: "รับรองคำแปลไทย–อังกฤษ การสะกดชื่อตามพาสปอร์ต และเหตุผลที่ถูกตีกลับ",
    descEn: "TH–EN certified translation, passport-matching spelling, and rejection causes.",
    matchTh: /(แปล|คำแปล|ตีกลับ|สะกด|ภาษา)/,
    matchEn: /(translat|spelling|reject|language|certified copy)/i,
  },
  {
    slug: "corporate",
    titleTh: "เอกสารนิติบุคคลและธุรกิจ",
    titleEn: "Corporate & Business Documents",
    descTh: "หนังสือรับรองบริษัท DBD บอจ.5 งบการเงิน สัญญา และเอกสารส่งออก",
    descEn: "DBD affidavits, shareholder lists, financials, contracts and export papers.",
    matchTh: /(บริษัท|นิติบุคคล|dbd|บอจ|งบการเงิน|สัญญา|ส่งออก|ธุรกิจ)/i,
    matchEn: /(company|corporate|dbd|affidavit|shareholder|financial|contract|export|business)/i,
  },
];

export function getConsularCategoryDef(slug: string): CategoryDef | undefined {
  return CONSULAR_FAQ_CATEGORIES.find((c) => c.slug === slug);
}
